import { useEffect, useRef, useState, useCallback } from "react";
import { Bot, Send, X, Loader2, Eraser, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = "uid_chatbot_session_id";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sid);
  }
  return sid;
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [consent, setConsent] = useState({ pending: true, consent_marketing: false, phone: null });
  const [consentBusy, setConsentBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  const refreshConsent = useCallback(async () => {
    try {
      const r = await fetch(`${API}/chatbot/consent?session_id=${sessionId.current}`);
      if (r.ok) setConsent(await r.json());
    } catch { /* ignore — chip stays hidden when phone not yet captured */ }
  }, []);

  const toggleConsent = async () => {
    setConsentBusy(true);
    try {
      const r = await fetch(`${API}/chatbot/consent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, consent: !consent.consent_marketing }),
      });
      if (r.ok) setConsent(await r.json());
    } catch { /* silent — non-critical */ }
    finally { setConsentBusy(false); }
  };

  // Load history once when opened
  useEffect(() => {
    if (!open) return;
    refreshConsent();
    if (messages.length > 0) return;
    fetch(`${API}/chatbot/history?session_id=${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.messages?.length) {
          setMessages(d.messages);
        } else {
          setMessages([
            {
              role: "assistant",
              content:
                "Hi! I'm UID Assist. Ask me anything about our IT services, PVC card printing, UID Care subscription, or video consultations.",
            },
          ]);
        }
      })
      .catch(() => {
        setMessages([
          { role: "assistant", content: "Hi! I'm UID Assist. How can I help?" },
        ]);
      });
  }, [open, messages.length, refreshConsent]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  async function handleSend(e) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/chatbot/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      // Stream SSE chunks
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data:\s?/, "").trim();
          if (!line || line === "[DONE]") continue;
          if (line.startsWith("[error:")) {
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { role: "assistant", content: line };
              return next;
            });
            continue;
          }
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, content: (last.content || "") + line };
            return next;
          });
        }
      }
    } catch (err) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "Sorry — I couldn't reach the server. Please try again or open a support ticket at /request.",
        };
        return next;
      });
    } finally {
      setSending(false);
      // After each round-trip, re-check if the backend detected a phone → show consent chip
      refreshConsent();
    }
  }

  async function handleClear() {
    if (sending) return;
    try {
      await fetch(`${API}/chatbot/history?session_id=${sessionId.current}`, {
        method: "DELETE",
      });
    } catch {
      /* ignore */
    }
    setMessages([
      { role: "assistant", content: "Fresh start! How can I help you today?" },
    ]);
  }

  return (
    <>
      {/* Launcher — sits to the left of the WhatsApp float */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open UID Assist chatbot"
          data-testid="chatbot-launcher"
          className="fixed bottom-6 right-24 z-50 grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-[0_10px_30px_-10px_rgba(251,146,60,0.7)] hover:scale-110 transition-transform"
          title="Chat with UID Assist (AI)"
        >
          <Bot className="h-7 w-7" />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[380px] h-[78vh] max-h-[560px] flex flex-col rounded-2xl border border-amber-500/30 bg-[#0F1626] text-white shadow-2xl shadow-amber-500/10 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-gradient-to-r from-amber-600/30 to-orange-600/20">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-amber-500 text-[#0A101D]">
                <Bot className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold">UID Assist</div>
                <div className="text-[11px] text-white/60">Powered by Claude · usually replies in seconds</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear chat"
                data-testid="chatbot-clear"
                className="grid h-8 w-8 place-items-center rounded-full hover:bg-white/10"
                title="Clear conversation"
              >
                <Eraser className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close chatbot"
                data-testid="chatbot-close"
                className="grid h-8 w-8 place-items-center rounded-full hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 whitespace-pre-wrap leading-relaxed ${
                    m.role === "user"
                      ? "bg-amber-500 text-[#0A101D] rounded-br-sm"
                      : "bg-white/10 text-white rounded-bl-sm"
                  }`}
                >
                  {m.content || (m.role === "assistant" && sending ? "…" : "")}
                </div>
              </div>
            ))}
            {sending && messages[messages.length - 1]?.content === "" && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-white/10 px-3 py-2 text-white/70">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Consent chip — grows the WhatsApp broadcast list, only visible after phone captured */}
          {!consent.pending && (
            <div className="px-2.5 pt-2">
              <button
                type="button"
                onClick={toggleConsent}
                disabled={consentBusy}
                className={`w-full inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${
                  consent.consent_marketing
                    ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/15"
                    : "border-amber-400/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15"
                }`}
                data-testid="chatbot-consent-chip"
              >
                {consentBusy ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : consent.consent_marketing ? (
                  <><BellRing className="h-3 w-3" /> Deadline alerts ON · tap to stop</>
                ) : (
                  <><BellRing className="h-3 w-3" /> Get scheme deadline alerts on WhatsApp</>
                )}
              </button>
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={handleSend}
            className="border-t border-white/10 p-2 flex items-center gap-2 bg-[#0A101D]"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about services, pricing, support…"
              maxLength={2000}
              disabled={sending}
              data-testid="chatbot-input"
              className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              aria-label="Send message"
              data-testid="chatbot-send"
              className="grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-[#0A101D] disabled:opacity-40"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
